import React, { useState, useRef, useEffect } from 'react';
import { Upload, X, Loader2, Link as LinkIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { API } from '@/App';
import axios from 'axios';
import { compressImage, formatFileSize, isImageFile } from '@/utils/imageCompression';

/**
 * ImageUpload Component
 * Provides image upload with auto-compression AND URL paste option
 * 
 * @param {Object} props
 * @param {string} props.value - Current image URL
 * @param {function} props.onChange - Callback when image URL changes
 * @param {string} props.className - Additional CSS classes
 */
const ImageUpload = ({ value, onChange, className = '' }) => {
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState('');
  const [previewUrl, setPreviewUrl] = useState(value || '');
  const [urlInput, setUrlInput] = useState('');
  const [showUrlInput, setShowUrlInput] = useState(false);
  const fileInputRef = useRef(null);

  // Sync previewUrl with value prop when it changes externally
  useEffect(() => {
    if (value !== previewUrl) {
      setPreviewUrl(value || '');
    }
  }, [value]);

  const handleFileSelect = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!isImageFile(file)) {
      toast.error('Format file tidak didukung. Gunakan JPG, PNG, GIF, atau WebP.');
      return;
    }

    if (file.size > 32 * 1024 * 1024) {
      toast.error('Ukuran file terlalu besar. Maksimal 32MB.');
      return;
    }

    setIsUploading(true);
    setUploadProgress('Mengompresi gambar...');

    try {
      const originalSize = file.size;
      const compressedBlob = await compressImage(file, {
        maxWidth: 800,
        maxHeight: 800,
        quality: 0.8
      });
      
      const compressedSize = compressedBlob.size;
      
      setUploadProgress('Mengupload gambar...');

      const formData = new FormData();
      formData.append('image', compressedBlob, file.name.replace(/\.[^/.]+$/, '.jpg'));

      const response = await axios.post(`${API}/upload/image`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 30000,
      });

      const imageUrl = response.data.medium_url || response.data.url;
      
      setPreviewUrl(imageUrl);
      onChange(imageUrl);
      setShowUrlInput(false);
      
      toast.success(`Gambar berhasil diupload! (${formatFileSize(compressedSize)})`);
      
    } catch (error) {
      console.error('Upload error:', error);
      toast.error(error.response?.data?.detail || 'Gagal mengupload gambar');
    } finally {
      setIsUploading(false);
      setUploadProgress('');
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUrlSubmit = () => {
    const url = urlInput.trim();
    if (!url) {
      toast.error('Masukkan URL gambar');
      return;
    }
    
    if (!url.startsWith('http://') && !url.startsWith('https://')) {
      toast.error('URL harus diawali dengan http:// atau https://');
      return;
    }
    
    setPreviewUrl(url);
    onChange(url);
    setUrlInput('');
    setShowUrlInput(false);
    toast.success('URL gambar berhasil ditambahkan');
  };

  const handleRemoveImage = () => {
    setPreviewUrl('');
    setUrlInput('');
    onChange('');
  };

  const rawUrl = previewUrl || value || '';
  const hasImage = rawUrl && rawUrl.trim() !== '';
  // Use proxy for external URLs to bypass CORS/referrer blocking (e.g. ImgBB hotlink protection)
  const displayUrl = hasImage && rawUrl.startsWith('http')
    ? `${API}/upload/proxy?url=${encodeURIComponent(rawUrl)}`
    : rawUrl;

  return (
    <div className={`space-y-3 ${className}`}>
      {/* Image preview - Show if image exists */}
      {hasImage && (
        <div className="relative rounded-lg overflow-hidden border-2 border-indigo-200 dark:border-indigo-800 bg-slate-50 dark:bg-zinc-900 group">
          <img 
            src={displayUrl} 
            alt="Preview" 
            className="w-full h-40 object-contain mx-auto bg-white dark:bg-[#0A0A0A]"
            loading="eager"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'data:image/svg+xml,<svg xmlns="http://www.w3.org/2000/svg" width="200" height="150" viewBox="0 0 200 150"><rect fill="%23fef2f2" width="200" height="150"/><text x="50%" y="50%" dominant-baseline="middle" text-anchor="middle" fill="%23ef4444" font-size="12">Gambar tidak dapat dimuat</text></svg>';
            }}
          />
          <button
            type="button"
            onClick={handleRemoveImage}
            className="absolute top-2 right-2 p-1.5 bg-red-500 text-white rounded-full shadow-lg hover:bg-red-600 transition-colors"
            title="Hapus gambar"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Hidden file input */}
      <input
        ref={fileInputRef}
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp"
        onChange={handleFileSelect}
        className="hidden"
        disabled={isUploading}
      />

      {/* Upload buttons - Only show if no image */}
      {!hasImage && (
        <div className="space-y-3">
          {/* Upload from device button */}
          <Button
            type="button"
            variant="outline"
            size="sm"
            onClick={() => fileInputRef.current?.click()}
            disabled={isUploading}
            className="w-full border-dashed border-2 h-auto py-4 hover:border-blue-500 hover:bg-indigo-50 dark:hover:bg-blue-950/20 dark:bg-indigo-950/40"
          >
            {isUploading ? (
              <div className="flex items-center gap-2">
                <Loader2 className="w-4 h-4 animate-spin" />
                <span className="text-sm">{uploadProgress}</span>
              </div>
            ) : (
              <div className="flex flex-col items-center gap-1">
                <Upload className="w-5 h-5" />
                <span className="text-sm font-medium">Upload dari Device</span>
                <span className="text-xs text-slate-500 dark:text-zinc-400">JPG, PNG, GIF, WebP (max 32MB)</span>
              </div>
            )}
          </Button>

          {/* Divider */}
          <div className="relative">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-slate-200 dark:border-zinc-800"></div>
            </div>
            <div className="relative flex justify-center text-xs">
              <span className="px-2 bg-white dark:bg-[#0A0A0A] text-slate-500 dark:text-zinc-400">atau</span>
            </div>
          </div>

          {/* URL Input toggle / input */}
          {!showUrlInput ? (
            <Button
              type="button"
              variant="ghost"
              size="sm"
              onClick={() => setShowUrlInput(true)}
              className="w-full text-slate-500 hover:text-slate-700 dark:text-zinc-200"
            >
              <LinkIcon className="w-4 h-4 mr-2" />
              Paste URL Gambar
            </Button>
          ) : (
            <div className="flex gap-2">
              <Input
                value={urlInput}
                onChange={(e) => setUrlInput(e.target.value)}
                placeholder="https://example.com/image.jpg"
                className="flex-1"
                onKeyDown={(e) => e.key === 'Enter' && handleUrlSubmit()}
              />
              <Button
                type="button"
                size="sm"
                onClick={handleUrlSubmit}
                className="px-4"
              >
                OK
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => { setShowUrlInput(false); setUrlInput(''); }}
              >
                <X className="w-4 h-4" />
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Change image button - Only show if image exists */}
      {hasImage && (
        <Button
          type="button"
          variant="outline"
          size="sm"
          onClick={() => fileInputRef.current?.click()}
          disabled={isUploading}
          className="w-full"
        >
          {isUploading ? (
            <div className="flex items-center gap-2">
              <Loader2 className="w-4 h-4 animate-spin" />
              <span className="text-sm">{uploadProgress}</span>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <Upload className="w-4 h-4" />
              <span className="text-sm">Ganti Gambar</span>
            </div>
          )}
        </Button>
      )}
    </div>
  );
};

export default ImageUpload;
